/// IALA region geography + Inland Waterways extras — content for the
/// Reference panels. The IalaMark data model already supports Region
/// A and B inversion via the bodyForRegion / lightForRegion helpers;
/// this file holds the supplementary material that explains the
/// distinction and adds the European Inland Waterways (IWW) marks.

import type { Region } from "./code";

// ───────────────────────────────────────────────────────────────────
// Regional geography
// ───────────────────────────────────────────────────────────────────

export interface RegionGeography {
  region: Region;
  /// Which countries / waters use this region.
  countries: readonly string[];
  /// The "returning" mnemonic for the region.
  mnemonic: string;
  /// One-line summary.
  summary: string;
  /// Lateral-mark colour convention.
  portConvention: string;
  starboardConvention: string;
}

export const REGION_GEOGRAPHY: readonly RegionGeography[] = Object.freeze([
  {
    region: "A",
    countries: [
      "All of Europe (UK, Ireland, France, Germany, Spain, Italy, Netherlands, Scandinavia, Greece, etc.)",
      "Africa (entire continent)",
      "Asia (most — including India, Indonesia, Saudi Arabia, UAE)",
      "Australia + New Zealand",
      "Pacific Islands (most)",
    ],
    mnemonic:
      "\"Is there any RED PORT left in the bottle?\" — RED to PORT when returning.",
    summary:
      "Region A — the convention used everywhere except the Americas + a few Asian countries. RED + can shape on PORT, GREEN + cone shape on STARBOARD, when entering harbour (returning).",
    portConvention:
      "PORT — Red, can-shaped body (or pillar), red can topmark, red light.",
    starboardConvention:
      "STARBOARD — Green, cone-shaped body (or pillar), green cone topmark point-up, green light.",
  },
  {
    region: "B",
    countries: [
      "North America (USA, Canada, Mexico)",
      "South America (Colombia, Brazil, Chile, Peru — most)",
      "Central America + Caribbean (most)",
      "Philippines",
      "Japan + South Korea + Taiwan",
    ],
    mnemonic:
      "\"Red Right Returning\" — RED to STARBOARD (right) when returning.",
    summary:
      "Region B — the convention used in the Americas + Japan + Philippines + Korea + Taiwan. RED + cone shape on STARBOARD, GREEN + can shape on PORT, when entering harbour (returning). The mirror image of Region A for LATERAL MARKS ONLY — cardinals, safe water, isolated danger + emergency wreck are identical worldwide.",
    portConvention:
      "PORT — Green, can-shaped body (or pillar), green can topmark, green light.",
    starboardConvention:
      "STARBOARD — Red, cone- or nun-shaped body, red conical topmark, red light.",
  },
]);

// ───────────────────────────────────────────────────────────────────
// Key differences summary — used as the headline content of the
// regional comparison panel.
// ───────────────────────────────────────────────────────────────────

export const REGION_DIFFERENCES: readonly string[] = Object.freeze([
  "LATERAL marks (port + starboard, preferred channel) — COLOURS + SHAPES inverted between Region A and Region B.",
  "CARDINAL marks (north, east, south, west) — IDENTICAL in both regions; black + yellow bands with double-cone topmarks.",
  "SAFE-WATER marks — IDENTICAL; red + white vertical stripes, spherical topmark, isophase / occulting / morse-A white light.",
  "ISOLATED-DANGER marks — IDENTICAL; black with red horizontal band, two-black-sphere topmark, group flashing white light (Fl(2)).",
  "SPECIAL marks (yellow) — IDENTICAL; yellow body, yellow X topmark, yellow light.",
  "EMERGENCY WRECK marks (blue + yellow) — IDENTICAL; alternating blue + yellow flashing light (Al.BuY 3s).",
  "LIGHT RHYTHMS — IDENTICAL in both regions. Only the COLOUR of the light on a lateral mark changes (red ↔ green).",
  "Direction of buoyage — chart symbol `>>>` shows which way to take port-on-left vs port-on-right when not following the natural 'returning to harbour' direction.",
]);

// ───────────────────────────────────────────────────────────────────
// European Inland Waterways (IWW) marks — CEVNI / SIGNI signs used
// on European inland waterways. Distinct from the marine IALA system
// and important for canal + river cruising in Europe.
// ───────────────────────────────────────────────────────────────────

export type IwwSign =
  | "prohibited"
  | "compulsory"
  | "restriction"
  | "permissive"
  | "indication";

export interface IwwMark {
  id: string;
  name: string;
  category: IwwSign;
  /// Shape + colour description.
  appearance: string;
  /// What the sign means.
  meaning: string;
  /// Where you typically encounter it.
  context: string;
}

export const IWW_MARKS: readonly IwwMark[] = Object.freeze([
  {
    id: "prohibited-entry",
    name: "Entry prohibited",
    category: "prohibited",
    appearance: "Square red sign with a single red horizontal bar across the centre.",
    meaning: "Vessels prohibited from entering / proceeding past this point.",
    context: "Closed locks, weirs, restricted military zones, harbours during maintenance.",
  },
  {
    id: "prohibited-overtaking",
    name: "Overtaking prohibited",
    category: "prohibited",
    appearance: "Square red sign with two black silhouetted vessels side by side.",
    meaning: "No overtaking — channel too narrow / visibility-limited section.",
    context: "Narrow canal sections, blind bends, lock approaches.",
  },
  {
    id: "prohibited-meeting",
    name: "Meeting + overtaking prohibited",
    category: "prohibited",
    appearance: "Square red sign with two black silhouetted vessels passing each other.",
    meaning: "Channel only wide enough for one direction at a time — wait for opposing traffic.",
    context: "Very narrow canals, single-channel sections, swing bridges.",
  },
  {
    id: "compulsory-stop",
    name: "Compulsory stop",
    category: "compulsory",
    appearance: "Square red sign with the word \"STOP\" in white block letters.",
    meaning: "STOP at this point — typically before a lock or moveable bridge.",
    context: "Lock approaches, customs posts, bridge controllers.",
  },
  {
    id: "compulsory-VHF",
    name: "Compulsory VHF contact",
    category: "compulsory",
    appearance: "Square red sign showing a stylised microphone or VHF set, often with the channel number below.",
    meaning: "Contact the lock or bridge controller by VHF on the indicated channel before proceeding.",
    context: "Lock approaches on major commercial waterways (Rhine, Danube).",
  },
  {
    id: "restriction-speed",
    name: "Speed limit",
    category: "restriction",
    appearance: "Square red sign with a black numeral indicating maximum speed in km/h.",
    meaning: "Maximum speed restriction over the ground.",
    context: "Marinas, town stretches, bird sanctuaries, lock approaches.",
  },
  {
    id: "restriction-depth",
    name: "Limited depth ahead",
    category: "restriction",
    appearance: "Square red sign with white triangle pointing down + numeric depth in metres below.",
    meaning: "Channel depth is reduced ahead — adjust draft + speed.",
    context: "Drying sections of canals + canal-river transitions.",
  },
  {
    id: "permissive-anchor",
    name: "Anchoring permitted",
    category: "permissive",
    appearance: "Square blue sign with white anchor symbol.",
    meaning: "Anchoring is permitted here.",
    context: "Designated anchorages on rivers + broader canals.",
  },
  {
    id: "permissive-mooring",
    name: "Mooring permitted",
    category: "permissive",
    appearance: "Square blue sign with white \"P\" (parking) symbol, often with a bollard pictogram.",
    meaning: "Mooring against the bank is permitted — typically a designated stretch.",
    context: "Town moorings, recreational stretches, fuel berths.",
  },
  {
    id: "indication-channel",
    name: "Channel cross or junction ahead",
    category: "indication",
    appearance: "Square red sign with white horizontal triangle pointing up + diagonal lines.",
    meaning: "Cross-channel or junction ahead — be alert for crossing traffic.",
    context: "Where two canals or rivers meet, often where commercial vessels turn.",
  },
  {
    id: "indication-overhead-cable",
    name: "Overhead cable / power line",
    category: "indication",
    appearance: "Square white sign with red border + black lightning-bolt + downward triangle, often with clearance height below.",
    meaning: "Overhead power cable crossing — risk of arcing to mast / rigging if too close.",
    context: "Canal + river crossings of power transmission lines.",
  },
]);

// ───────────────────────────────────────────────────────────────────
// Direction of buoyage — chart symbol convention
// ───────────────────────────────────────────────────────────────────

export const DIRECTION_OF_BUOYAGE_NOTE = [
  "When NOT entering harbour ('returning'), the direction of buoyage is defined by the chart.",
  "Look for the arrow symbol — a red triangular arrow with a dot above (Region A: red on port-hand side; Region B: red on starboard-hand side).",
  "Where the arrow points = the direction in which lateral marks follow the convention.",
  "Without an explicit arrow on the chart, the 'returning' convention is: into the harbour, upstream on a river, clockwise round a continent (clockwise N hem, anticlockwise S hem on some IMO-published guidance).",
  "When crossing between regions (e.g. a transatlantic passage) — switch your mental model AT THE MOMENT you cross between the buoyage authorities of the two regions, typically marked on charts as a 'change of buoyage' note.",
].join(" ");
